/**
 * Drop-in bridge for product apps.
 * Copy skin-pack.ts + runtime.ts + this file into themes/ and call
 *   import { wireSuiteSkins } from "./suiteBridge";
 *   wireSuiteSkins({ system: "pf", storageKey: "…", applyBuiltin, getDefault });
 * from bootstrapTheme / main.
 */

import {
  applyPack,
  bootstrapSuiteSkins,
  findPack,
  getOfficialMotionIds,
  getOfficialMotions,
  getOfficialSkinIds,
  loadCachedPrefs,
  loadCachedRegistry,
  notifySuiteSkins,
  SUITE_SKINS_EVENT,
  setActivePrefs,
  type TokenSystem,
} from "./runtime";
import type { SkinPack } from "./skin-pack";

export { SUITE_SKINS_EVENT, getOfficialMotionIds, getOfficialMotions, getOfficialSkinIds, notifySuiteSkins };

export interface WireOptions {
  system: TokenSystem;
  storageKey: string;
  applyBuiltin: (id: string) => void;
  getDefault: () => string;
  /** ProxyPro / AccessHub origin, e.g. https://proxy.vividlex.com */
  apiBaseUrl?: string;
  productAccent?: string;
  /** Called when a suite pack was applied (for Settings UI refresh) */
  onPackApplied?: (pack: SkinPack) => void;
}

const DEFAULT_API =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_SUITE_SKINS_URL) ||
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_PROXYPRO_URL) ||
  "https://proxy.vividlex.com";

/**
 * Resolve theme id from builtins or suite cache.
 * Use in getTheme fallbacks when isThemeId is false.
 */
export function resolveSuitePack(id: string): SkinPack | null {
  return findPack(loadCachedRegistry(), id);
}

export function listSuitePacks(): SkinPack[] {
  const r = loadCachedRegistry();
  return [...r.suite, ...r.user];
}

export type PickerTheme = {
  id: string;
  name: string;
  description: string;
  mode: "light" | "dark";
  preview: [string, string, string];
};

/** Neon AccessHub catalog packs, in catalog order. Builtins only if the catalog is empty. */
export function listPickerThemes(
  builtins: PickerTheme[],
): PickerTheme[] {
  const packs = listSuitePacks();
  if (packs.length) {
    return packs.map((p) => ({
      id: p.id,
      name: p.name || p.id,
      description: p.description || p.name || p.id,
      mode: p.mode === "light" ? "light" : "dark",
      preview: p.preview || ["#111", "#888", "#eee"],
    }));
  }
  return builtins;
}

export function listPickerMotions<T extends { id: string; name: string }>(
  builtins: T[],
): T[] {
  const official = getOfficialMotions();
  if (official && official.length) {
    return official.map((m) => {
      const hit = builtins.find((b) => b.id === m.id);
      if (hit) return { ...hit, name: m.name };
      return { id: m.id, name: m.name } as T;
    });
  }
  const allowed = getOfficialMotionIds();
  if (!allowed || !allowed.size) return builtins;
  return builtins.filter(
    (m) => allowed.has(m.id) || m.id === "none" || m.id === "still",
  );
}

export function selectSuiteOrBuiltin(
  id: string,
  opts: Pick<WireOptions, "system" | "storageKey" | "applyBuiltin" | "productAccent">,
): string {
  const pack = resolveSuitePack(id);
  if (pack) {
    applyPack(pack, opts.system, {
      storageKey: opts.storageKey,
      productAccent: opts.productAccent,
    });
    setActivePrefs(pack.id, pack.defaultFx);
    return pack.id;
  }
  opts.applyBuiltin(id);
  setActivePrefs(id, null);
  return id;
}

/** Call once at app boot (after DOM ready). */
export function wireSuiteSkins(opts: WireOptions): void {
  const apiBaseUrl = opts.apiBaseUrl || DEFAULT_API || undefined;

  void bootstrapSuiteSkins({
    system: opts.system,
    applyBuiltin: opts.applyBuiltin,
    getBuiltinDefault: opts.getDefault,
    storageKey: opts.storageKey,
    apiBaseUrl,
    productAccent: opts.productAccent,
  }).then(({ pack }) => {
    if (pack && opts.onPackApplied) opts.onPackApplied(pack);
    notifySuiteSkins();
  });

  // If prefs point at a cached pack, apply immediately (before network)
  const prefs = loadCachedPrefs();
  if (prefs?.activeSkinId) {
    const pack = resolveSuitePack(prefs.activeSkinId);
    if (pack) {
      applyPack(pack, opts.system, {
        storageKey: opts.storageKey,
        productAccent: opts.productAccent,
      });
      if (prefs.activeFxId) {
        document.documentElement.setAttribute("data-bg-fx", prefs.activeFxId);
      }
    }
  }
}

export type { SkinPack };
